// @ts-nocheck

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { EventFormData } from "../CreateEventForm";

interface VenueLocationTabProps {
  formData: EventFormData;
  updateFormData: (data: Partial<EventFormData>) => void;
}

interface Venue {
  id: string;
  name: string;
  city: string;
  state: string;
}

const VenueLocationTab: React.FC<VenueLocationTabProps> = ({ formData, updateFormData }) => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddingVenue, setIsAddingVenue] = useState(false);
  const [newVenue, setNewVenue] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    phone: "",
    website: ""
  });

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenues = async () => {
    try {
      const { data, error } = await supabase
        .from('venues')
        .select('id, name, city, state')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setVenues(data || []);
    } catch (error) {
      console.error('Error fetching venues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMainVenueChange = (venueId: string) => {
    console.log('🎯 Main venue selected:', venueId);
    console.log('🎯 Current formData before update:', formData);
    const venue = venues.find((v) => v.id === venueId);
    
    updateFormData({ 
      main_venue_id: venueId,
      venue_id: venueId,
      venue_name: venue?.name || '',
      venue_city: venue?.city || '',
      venue_state: venue?.state || ''
    });
    
    console.log('🎯 updateFormData called with:', { main_venue_id: venueId, venue_id: venueId });
  };

  const handleAdditionalVenueChange = (venueId: string, checked: boolean) => {
    const currentAdditionalVenues = formData.additional_venue_ids || [];
    
    if (checked) {
      updateFormData({ 
        additional_venue_ids: [...currentAdditionalVenues, venueId] 
      });
    } else {
      updateFormData({ 
        additional_venue_ids: currentAdditionalVenues.filter(id => id !== venueId) 
      });
    }
  };

  const handleNewVenueChange = (field: string, value: string) => {
    setNewVenue(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveNewVenue = async () => {
    if (!newVenue.name.trim()) {
      toast.error("Venue name is required");
      return;
    }

    try {
      setIsAddingVenue(true);
      
      const venueData = {
        name: newVenue.name.trim(),
        address: newVenue.address.trim() || null,
        city: newVenue.city.trim() || null,
        state: newVenue.state.trim() || null,
        zip_code: newVenue.zip_code.trim() || null,
        phone: newVenue.phone.trim() || null,
        website: newVenue.website.trim() || null
      };

      const { data, error } = await supabase
        .from('venues')
        .insert([venueData])
        .select('id, name, city, state')
        .single();

      if (error) throw error;

      // Add to venues list
      setVenues(prev => [...prev, data]);
      updateFormData({
        main_venue_id: data.id,
        venue_id: data.id,
        venue_name: data.name || '',
        venue_city: data.city || '',
        venue_state: data.state || ''
      });
      
      // Reset form
      setNewVenue({
        name: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        phone: "",
        website: ""
      });
      
      toast.success("Venue added successfully!");
    } catch (error) {
      console.error('Error creating venue:', error);
      toast.error("Failed to create venue");
    } finally {
      setIsAddingVenue(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Venue Selection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Tournament Headquarters</Label>
            {loading ? (
              <div className="text-sm text-gray-500">Loading venues...</div>
            ) : (
              <Select 
                value={formData.main_venue_id || formData.venue_id || ''} 
                onValueChange={handleMainVenueChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tournament headquarters" />
                </SelectTrigger>
                <SelectContent>
                  {venues.map((venue) => (
                    <SelectItem key={venue.id} value={venue.id}>
                      {venue.name} - {venue.city}, {venue.state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Debug info - remove after testing */}
            <div className="text-xs text-gray-500 mt-2">
              Debug: main_venue_id = {formData.main_venue_id || 'undefined'}, venue_id = {formData.venue_id || 'undefined'}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Additional Venues</Label>
            {loading ? (
              <div className="text-sm text-gray-500">Loading venues...</div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {venues
                  .filter(venue => venue.id !== (formData.main_venue_id || formData.venue_id))
                  .map((venue) => (
                  <div key={venue.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`additional-venue-${venue.id}`}
                      checked={formData.additional_venue_ids?.includes(venue.id) || false}
                      onCheckedChange={(checked) => handleAdditionalVenueChange(venue.id, checked as boolean)}
                    />
                    <Label htmlFor={`additional-venue-${venue.id}`} className="text-sm">
                      {venue.name} - {venue.city}, {venue.state}
                    </Label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Add New Venue</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="venue-name">Venue Name *</Label>
              <Input
                id="venue-name"
                value={newVenue.name}
                onChange={(e) => handleNewVenueChange('name', e.target.value)}
                placeholder="Enter venue name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="venue-address">Address</Label>
              <Input
                id="venue-address"
                value={newVenue.address}
                onChange={(e) => handleNewVenueChange('address', e.target.value)}
                placeholder="Enter address"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="venue-city">City</Label>
              <Input
                id="venue-city"
                value={newVenue.city}
                onChange={(e) => handleNewVenueChange('city', e.target.value)}
                placeholder="Enter city"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="venue-state">State</Label>
              <Input
                id="venue-state"
                value={newVenue.state}
                onChange={(e) => handleNewVenueChange('state', e.target.value)}
                placeholder="Enter state"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="venue-zip">ZIP Code</Label>
              <Input
                id="venue-zip"
                value={newVenue.zip_code}
                onChange={(e) => handleNewVenueChange('zip_code', e.target.value)}
                placeholder="Enter ZIP code"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="venue-phone">Phone</Label>
              <Input
                id="venue-phone"
                value={newVenue.phone}
                onChange={(e) => handleNewVenueChange('phone', e.target.value)}
                placeholder="Enter phone number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="venue-website">Website</Label>
              <Input
                id="venue-website"
                value={newVenue.website}
                onChange={(e) => handleNewVenueChange('website', e.target.value)}
                placeholder="Enter website URL"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button 
              type="button" 
              onClick={saveNewVenue}
              disabled={isAddingVenue || !newVenue.name.trim()}
              variant="outline"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              {isAddingVenue ? "Adding..." : "Add Venue"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VenueLocationTab;